import { Form } from 'antd';
import { useCallback, useMemo, useRef, useState } from 'react';

import { maskRules } from '@app/components/Phone/constants/masks';
import type {
  PhoneValidation,
  UsePhoneProps,
} from '@app/components/Phone/types';
import {
  applyMask,
  cleanMask,
  deduplicatePastedPhone,
  getPhoneDigits,
  getPhoneErrorMessage,
  isRussianLocalPhoneNumber,
  normalizeRussianLocalPhoneInput,
} from '@app/components/Phone/utils';
import { selectCountries, useAppSelector } from '@app/store';
import { type NDictionaries, CountryAlpha2 } from '@app/types';

const { useFormInstance, useWatch } = Form;
export const usePhone = ({
  countries,
  name = 'Phone',
  required = false,
  disabled = false,
  onFinish,
  onValidate,
  readOnly,
  onCountryListOpenChange,
}: UsePhoneProps) => {
  const form = useFormInstance();
  const { setFieldsValue, getFieldValue } = form;

  const [isEdited, setIsEdited] = useState(false);
  const savedCountries = useAppSelector(selectCountries);
  const [popoverWidth, setPopoverWidth] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [validation, setValidation] = useState<PhoneValidation>({
    isValid: false,
    errorMessage: null,
  });
  const [maskedPhone, setMaskedPhone] = useState<string>('');
  const phoneValue: { Phone: string; CountryId: number } = useWatch(name, form);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastFinishedPhoneRef = useRef<string>('');
  const [popoverVisible, setPopoverVisible] = useState(false);

  const availableCountries = useMemo(
    () => countries || savedCountries,
    [countries, savedCountries]
  );

  const unknownCountry = useMemo(
    () =>
      availableCountries.find((c) => c.Alpha2 === CountryAlpha2.UNKNOWN) ||
      null,
    [availableCountries]
  );

  const [selectedCountry, setSelectedCountry] =
    useState<NDictionaries.ModelCountry | null>(() => unknownCountry);

  // Список стран открывается и закрывается только здесь: владелец поля
  // узнаёт об этом сразу, до фокуса в поле (task/11629).
  const changePopoverVisible = useCallback(
    (open: boolean) => {
      setPopoverVisible(open);
      onCountryListOpenChange?.(open);
    },
    [onCountryListOpenChange]
  );

  const popoverVisibleHandler = useCallback(
    (open: boolean) => {
      if (disabled || readOnly) return;
      changePopoverVisible(open);
    },
    [disabled, readOnly, changePopoverVisible]
  );
  // Закрытое поле не меняет страну: флаг не открывает список стран.
  const togglePopover = () => {
    if (readOnly) return;
    changePopoverVisible(!popoverVisible);
  };

  const updatePopoverWidthHandler = useCallback(() => {
    if (containerRef.current) {
      setPopoverWidth(containerRef.current.offsetWidth);
    }
  }, []);

  const identifyCountryAlpha = useCallback(
    (value: string) => {
      const availableCountriesWithoutUnknown = availableCountries.filter(
        (c) => c.Alpha2 !== CountryAlpha2.UNKNOWN
      );

      const matchedCodes = availableCountriesWithoutUnknown.filter(
        ({ TelCode }) => value.startsWith(TelCode)
      );

      if (value.startsWith('7')) {
        const isKazakhstan = value.startsWith('77');
        if (isKazakhstan) {
          return CountryAlpha2.KZ;
        }
        return CountryAlpha2.RU;
      }

      if (value.startsWith('1')) {
        if (selectedCountry?.Alpha2 === CountryAlpha2.US) {
          return CountryAlpha2.US;
        }
        if (selectedCountry?.Alpha2 === CountryAlpha2.CA) {
          return CountryAlpha2.CA;
        }
        return CountryAlpha2.US;
      }

      if (matchedCodes.length === 0) {
        return CountryAlpha2.UNKNOWN;
      }
      const matchedCode = matchedCodes[0].Alpha2;

      return matchedCode;
    },
    [availableCountries, selectedCountry]
  );

  const normalizePhoneValue = useCallback(
    (value: string) => {
      const phoneDigits = getPhoneDigits(value);
      const hasExplicitInternationalPrefix = value.trim().startsWith('+');
      const hasSelectedCountryCode =
        !!selectedCountry?.TelCode &&
        phoneDigits.startsWith(selectedCountry.TelCode);

      if (hasExplicitInternationalPrefix) {
        const explicitCountryAlpha = identifyCountryAlpha(phoneDigits);

        if (explicitCountryAlpha !== CountryAlpha2.UNKNOWN) {
          return value;
        }
      }

      if (hasSelectedCountryCode) {
        return value;
      }

      if (isRussianLocalPhoneNumber(phoneDigits)) {
        return normalizeRussianLocalPhoneInput(value);
      }

      return value;
    },
    [identifyCountryAlpha, selectedCountry]
  );

  const initValue = useCallback(() => {
    if (!form) return;
    if (phoneValue === undefined) return;
    if (disabled) return;
    if (isInitialized) return;
    // Defer initialization until the countries dictionary is available.
    // Otherwise the field would mark itself initialized with no resolvable
    // country (empty list) and never recover once countries load later,
    // leaving the country code blank in a freshly opened record modal.
    if (availableCountries.length === 0) return;
    const value = phoneValue?.Phone || '';
    const countryId = phoneValue?.CountryId || null;

    const validValue = normalizePhoneValue(value);
    const withoutMask = cleanMask(validValue);
    const countryAlpha = identifyCountryAlpha(withoutMask);
    const country =
      (!!countryId
        ? availableCountries.find((c) => c.Id === countryId) || null
        : availableCountries.find((c) => c.Alpha2 === countryAlpha) || null) ||
      unknownCountry;

    if (!country) {
      setSelectedCountry(unknownCountry);
      setMaskedPhone('');
      setIsInitialized(true);
      return;
    }

    const { Alpha2, TelCode } = country;
    setSelectedCountry(country);
    const mask = maskRules[Alpha2];
    const Phone = withoutMask || TelCode || '';
    const maskedValue = applyMask(Phone, mask);
    setMaskedPhone(maskedValue);
    setIsInitialized(true);
  }, [
    form,
    isInitialized,
    disabled,
    phoneValue,
    availableCountries,
    normalizePhoneValue,
    identifyCountryAlpha,
    unknownCountry,
  ]);

  const onChangeHandler = useCallback(
    (value: string) => {
      if (!isEdited) {
        setIsEdited(true);
      }
      const validValue = normalizePhoneValue(value);
      const withoutMask = cleanMask(validValue);

      if (withoutMask.startsWith('0')) {
        return;
      }

      const countryAlpha = identifyCountryAlpha(withoutMask);
      const country =
        availableCountries.find((c) => c.Alpha2 === countryAlpha) || null;

      setSelectedCountry(country);
      const filteredValue = withoutMask.replace(/[^\d+]/g, '');

      const mask = maskRules[countryAlpha];
      const maskedValue = applyMask(filteredValue, mask);

      setMaskedPhone(maskedValue);
      setFieldsValue({
        [name]: {
          Phone: filteredValue,
          CountryId: country?.Id || null,
        },
      });
    },
    [
      setFieldsValue,
      identifyCountryAlpha,
      name,
      availableCountries,
      normalizePhoneValue,
      isEdited,
    ]
  );

  // Вставка целого номера при выбранной стране: дубль кода страны из буфера
  // вырезается, поле заменяется нормализованным номером (us/10647). Вставки,
  // не похожие на целый номер, проходят обычным путём ввода.
  const onPasteHandler = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const countryTelCode = selectedCountry?.TelCode;
      if (!countryTelCode) return;

      const replacementDigits = deduplicatePastedPhone({
        pastedText: event.clipboardData.getData('text'),
        countryTelCode,
        minDigits: selectedCountry?.MinDigits ?? null,
        maxDigits: selectedCountry?.MaxDigits ?? null,
      });
      if (!replacementDigits) return;

      event.preventDefault();
      onChangeHandler(`+${replacementDigits}`);
    },
    [onChangeHandler, selectedCountry]
  );

  const onSelectCountry = useCallback(
    (countryAlpha2: CountryAlpha2) => {
      if (readOnly) return;

      const prevCountry =
        availableCountries.find((c) => c.Alpha2 === selectedCountry?.Alpha2) ||
        null;

      const nextCountry =
        availableCountries.find((c) => c.Alpha2 === countryAlpha2) || null;

      setSelectedCountry(nextCountry);
      if (nextCountry) {
        const prevCode = prevCountry?.TelCode || '';
        const newCode = nextCountry.TelCode || '';

        if (prevCode === newCode) {
          if (
            nextCountry.Alpha2 === CountryAlpha2.KZ ||
            nextCountry.Alpha2 === CountryAlpha2.RU
          ) {
            setMaskedPhone(`+${newCode}`);
            setFieldsValue({
              [name]: {
                Phone: newCode,
                CountryId: nextCountry.Id,
              },
            });
            changePopoverVisible(false);
            return;
          }

          setFieldsValue({
            [name]: {
              Phone: getFieldValue(name)?.Phone || '',
              CountryId: nextCountry.Id,
            },
          });
          changePopoverVisible(false);

          return;
        }

        const value = `+${newCode}`;
        setMaskedPhone(value);
        setFieldsValue({
          [name]: {
            Phone: newCode,
            CountryId: nextCountry.Id,
          },
        });
      }
      changePopoverVisible(false);
    },
    [
      availableCountries,
      selectedCountry,
      setFieldsValue,
      name,
      getFieldValue,
      readOnly,
      changePopoverVisible,
    ]
  );

  const errorHandler = useCallback(() => {
    if (disabled) return;
    if (!isInitialized) return;
    const { MinDigits, MaxDigits, TelCode } = selectedCountry ?? {};
    if (!MinDigits || !MaxDigits) return;

    const { errorMessage, isValid } = getPhoneErrorMessage({
      maskedPhone,
      telCode: TelCode,
      minDigits: MinDigits,
      maxDigits: MaxDigits,
      required,
      isEdited,
    });

    setValidation((prev) => ({
      ...prev,
      isValid,
      errorMessage,
    }));
    form.setFields([
      {
        name,
        errors: errorMessage ? [errorMessage] : [],
      },
    ]);
  }, [
    disabled,
    selectedCountry,
    maskedPhone,
    required,
    isInitialized,
    isEdited,
    form,
    name,
  ]);

  const onFinishHandler = useCallback(() => {
    if (disabled) return;
    if (!selectedCountry) return;
    if (!isInitialized) return;
    if (!isEdited) return;

    const phone = cleanMask(maskedPhone);

    const isValidPhone =
      phone.length >= selectedCountry.MinDigits &&
      phone.length <= selectedCountry.MaxDigits;

    if (!isValidPhone) {
      lastFinishedPhoneRef.current = '';
      return;
    }

    if (lastFinishedPhoneRef.current === phone) {
      return;
    }

    lastFinishedPhoneRef.current = phone;
    onFinish?.(phone);
  }, [
    disabled,
    selectedCountry,
    maskedPhone,
    onFinish,
    isInitialized,
    isEdited,
  ]);

  const hasOnValidate = useMemo(
    () =>
      typeof onValidate === 'function' &&
      onValidate !== null &&
      onValidate !== undefined,
    [onValidate]
  );

  const onValidateHandler = useCallback(() => {
    if (!hasOnValidate) return;
    onValidate?.(validation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOnValidate, validation]);

  const onUpdatePhoneHandler = useCallback(() => {
    if (!isInitialized) return;
    const validValue = normalizePhoneValue(phoneValue?.Phone || '');
    const withoutMask = cleanMask(validValue);
    const countryId = phoneValue?.CountryId || null;
    const countryById = countryId
      ? availableCountries.find((c) => c.Id === countryId) || null
      : null;

    const savedPhone = cleanMask(maskedPhone);
    if (withoutMask === savedPhone && countryId === selectedCountry?.Id) {
      return;
    }

    if (withoutMask.startsWith('0')) {
      return;
    }

    const countryAlpha =
      countryById?.Alpha2 ?? identifyCountryAlpha(withoutMask);
    const country =
      countryById ||
      availableCountries.find((c) => c.Alpha2 === countryAlpha) ||
      null;

    setSelectedCountry(country);
    const filteredValue = withoutMask.replace(/[^\d+]/g, '');

    const mask = maskRules[countryAlpha];
    const maskedValue = applyMask(
      filteredValue || country?.TelCode || '',
      mask
    );

    setMaskedPhone(maskedValue);
    setFieldsValue({
      [name]: {
        ...phoneValue,
        Phone: filteredValue,
        CountryId: country?.Id || countryId,
      },
    });
  }, [
    availableCountries,
    identifyCountryAlpha,
    isInitialized,
    maskedPhone,
    name,
    normalizePhoneValue,
    phoneValue,
    selectedCountry?.Id,
    setFieldsValue,
  ]);

  return {
    containerRef,
    popoverVisible,
    popoverVisibleHandler,
    togglePopover,
    selectedCountry,
    popoverWidth,
    availableCountries,
    onSelectCountry,
    maskedPhone,
    validation,
    onChangeHandler,
    onPasteHandler,
    errorHandler,
    initValue,
    updatePopoverWidthHandler,
    onFinishHandler,
    onValidateHandler,
    onUpdatePhoneHandler,
  };
};
